import {applicationApiPath} from './applicationApiPath';
import { isPublicSimulationRuntime } from './publicSimulationRuntime';
import axios from "axios";
import type { AxiosResponse } from "axios";
import {apiAzureUrl, apiLocalHttp} from '@/uptk/core/api/contants'
import { getPreferredAccessToken } from '@/uptk/core/api/clerkTokenProvider';
import {
  buildSaasHeaders,
  resolveSaasContext,
  type SaasContext
} from "@/uptk/core/api/saasContext";

export default class BaseApi {

  static accessToken = '';
  static options = {};

  static getLocalBaseUrl() {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }

    return apiLocalHttp;
  }

  static normalizeContextValue(value: unknown) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length === 0) {
        return '';
      }
      const lowered = trimmed.toLowerCase();
      if (lowered === 'null' || lowered === 'undefined') {
        return '';
      }
      return trimmed;
    }

    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
      const normalized = `${value}`.trim();
      return normalized.length > 0 ? normalized : '';
    }

    return '';
  }

  static isPayloadRecord(value: unknown): value is Record<string, unknown> {
    return value != undefined && value !== null && typeof value === 'object' && Array.isArray(value) === false;
  }

  static extractPayloadContextValue(payload: unknown, keys: string[]) {
    if (!BaseApi.isPayloadRecord(payload)) {
      return '';
    }

    const parameters = BaseApi.isPayloadRecord(payload.parameters) ? payload.parameters : null;
    const rootValue = keys
      .map((key) => BaseApi.normalizeContextValue(payload[key]))
      .find((value) => value.length > 0);

    if (rootValue) {
      return rootValue;
    }

    return parameters
      ? keys
        .map((key) => BaseApi.normalizeContextValue(parameters[key]))
        .find((value) => value.length > 0) ?? ''
      : '';
  }

  static syncPayloadContextAliases(
    payload: Record<string, unknown>,
    keys: string[],
    fallbackValue: string
  ) {
    const resolvedValue = (
      keys
        .map((key) => BaseApi.normalizeContextValue(payload[key]))
        .find((value) => value.length > 0) ??
      BaseApi.normalizeContextValue(fallbackValue)
    );

    if (!resolvedValue) {
      return;
    }

    keys.forEach((key) => {
      if (!BaseApi.normalizeContextValue(payload[key])) {
        payload[key] = resolvedValue;
      }
    });
  }

  static applySaasContextToPayload(payload: unknown): unknown {
    if (typeof FormData !== 'undefined' && payload instanceof FormData) {
      return payload;
    }

    if (typeof payload === 'string') {
      const trimmed = payload.trim();
      if (!(trimmed.startsWith('{') && trimmed.endsWith('}'))) {
        return payload;
      }

      try {
        const parsedPayload = JSON.parse(trimmed) as unknown;
        const enrichedPayload = BaseApi.applySaasContextToPayload(parsedPayload);
        return typeof enrichedPayload === 'string'
          ? enrichedPayload
          : JSON.stringify(enrichedPayload);
      } catch {
        return payload;
      }
    }

    if (!BaseApi.isPayloadRecord(payload)) {
      return payload;
    }

    const nextPayload: Record<string, unknown> = {...payload};
    const parameters = BaseApi.isPayloadRecord(nextPayload.parameters)
      ? {...nextPayload.parameters}
      : null;
    const context = resolveSaasContext({
      tenantId: BaseApi.extractPayloadContextValue(nextPayload, ['tenantId', 'tenantid']),
      organizationId: BaseApi.extractPayloadContextValue(nextPayload, ['organizationId', 'organizationid']),
      workspaceId: BaseApi.extractPayloadContextValue(
        nextPayload,
        ['workspaceId', 'workspaceid', 'subscriptionId', 'subscriptionid']
      )
    });

    BaseApi.syncPayloadContextAliases(nextPayload, ['tenantId', 'tenantid'], context.tenantId);
    BaseApi.syncPayloadContextAliases(nextPayload, ['organizationId', 'organizationid'], context.organizationId);
    BaseApi.syncPayloadContextAliases(
      nextPayload,
      ['workspaceId', 'workspaceid', 'subscriptionId', 'subscriptionid'],
      context.workspaceId
    );

    if (parameters) {
      BaseApi.syncPayloadContextAliases(parameters, ['tenantId', 'tenantid'], context.tenantId);
      BaseApi.syncPayloadContextAliases(parameters, ['organizationId', 'organizationid'], context.organizationId);
      BaseApi.syncPayloadContextAliases(
        parameters,
        ['workspaceId', 'workspaceid', 'subscriptionId', 'subscriptionid'],
        context.workspaceId
      );
      nextPayload.parameters = parameters;
    }

    return nextPayload;
  }

  static resolveRequestContext(payload?: unknown): SaasContext {
    return resolveSaasContext({
      tenantId: BaseApi.extractPayloadContextValue(payload, ['tenantId', 'tenantid']),
      organizationId: BaseApi.extractPayloadContextValue(payload, ['organizationId', 'organizationid']),
      workspaceId: BaseApi.extractPayloadContextValue(
        payload,
        ['workspaceId', 'workspaceid', 'subscriptionId', 'subscriptionid']
      )
    });
  }

  static buildSaasQueryParams(context: SaasContext) {
    const params: Record<string, string> = {};

    if (context.tenantId) {
      params.tenantId = context.tenantId;
      params.tenantid = context.tenantId;
    }

    if (context.organizationId) {
      params.organizationId = context.organizationId;
      params.organizationid = context.organizationId;
    }

    if (context.workspaceId) {
      params.workspaceId = context.workspaceId;
      params.workspaceid = context.workspaceId;
    }

    return params;
  }

  static applyRequestContext(options: any, payload?: unknown) {
    const enrichedPayload = BaseApi.applySaasContextToPayload(payload);
    const context = BaseApi.resolveRequestContext(enrichedPayload);
    const existingHeaders = BaseApi.isPayloadRecord(options?.headers) ? options.headers : {};
    const existingParams = BaseApi.isPayloadRecord(options?.params) ? options.params : {};
    const saasParams = BaseApi.buildSaasQueryParams(context);
    const nextOptions = {
      ...options,
      headers: {
        ...existingHeaders,
        ...buildSaasHeaders(context)
      }
    } as any;

    if (Object.keys(existingParams).length > 0 || Object.keys(saasParams).length > 0) {
      nextOptions.params = {
        ...saasParams,
        ...existingParams
      };

      if (nextOptions.paramsSerializer == undefined) {
        nextOptions.paramsSerializer = (params: Record<string, string>) => {
          return new URLSearchParams(params).toString();
        };
      }
    }

    return {
      options: nextOptions,
      payload: enrichedPayload
    };
  }

  static getHeaders (accessToken: any) {
    if (isPublicSimulationRuntime()) return { Accept: 'Application/json', 'Content-Type': 'application/json;charset=utf-8' };

    let headers: Record<string, string> = {} // new Headers();

    headers = buildSaasHeaders(resolveSaasContext());

    const resolvedAccessToken = (
      BaseApi.normalizeContextValue(accessToken) ||
      BaseApi.normalizeContextValue(BaseApi.accessToken) ||
      BaseApi.normalizeContextValue(
        typeof window !== 'undefined' ? localStorage.getItem('accessToken') : ''
      )
    );

    if (resolvedAccessToken) {
      const bearer = `Bearer ${resolvedAccessToken}`;
      // headers.append("Authorization", bearer);
      headers["Authorization"] = bearer

    }

    // headers.append("Accept", 'Application/json');
    // headers.append("Content-Type", 'application/json');
    // headers.append("Content-Type", 'charset=utf-8"');


    headers["Accept"] = 'Application/json'
    headers["Content-Type"] =  'application/json;charset=utf-8'
    return headers;
  }

  static async getOptions(payload?: any) {

    const preferredAccessToken = await getPreferredAccessToken();
    const headers = BaseApi.getHeaders(preferredAccessToken);

    let options: any = {
      mode: "cors",
      credentials: 'same-origin',
      headers: headers
    };

    if (payload) {
        options.params = payload;
        options.paramsSerializer = (params: any) => {
          return new URLSearchParams(params).toString();
        }
    }
    return options
  }

  static async getLocal(path: any, payload?: any) {
    const options = await BaseApi.getOptions(payload)
    return axios
        .get( BaseApi.getLocalBaseUrl() + path, options);
  }

  static async get(path: any, payload?: any) {

    const options = await BaseApi.getOptions(payload);
    let fullPath = applicationApiPath(path,apiAzureUrl());
    return axios
        .get(fullPath, options);
  }

  static async getBlob(path: any) {

    const options = await BaseApi.getOptions(undefined);
    options.responseType = 'blob';

    return axios
        .get(applicationApiPath(path,apiAzureUrl()), options);
  }

  static async getExcelLocal(path: any) {
    const options = await BaseApi.getOptions(undefined)
    return axios
        .get( BaseApi.getLocalBaseUrl() + path, {responseType: 'blob', ...options});
  }

  static async getExcel(path: any) {

    const options = await BaseApi.getOptions(undefined);
    return axios
        .get(applicationApiPath(path,apiAzureUrl()), {responseType: 'blob', ...options} );
  }

  static async postExcelLocal(path: any, payload: any) {
    const options = await BaseApi.getOptions(undefined)
    options.responseType = 'blob'
    return axios
        .post( BaseApi.getLocalBaseUrl() + path, payload, options);
  }

  static async postExcel(path: any, payload: any) {

    const options = await BaseApi.getOptions(undefined);
    options.responseType = 'blob'
    return axios
        .post(applicationApiPath(path,apiAzureUrl()), payload, options );
  }

  static async postCsv(path: any, payload: any) {

    const options = await BaseApi.getOptions(undefined);
    return axios
        .post(applicationApiPath(path,apiAzureUrl()), payload, options );
  }

  static async postCsvLocal(path: any, payload: any) {
    
    const options = await BaseApi.getOptions(undefined)
    return axios
        .post( BaseApi.getLocalBaseUrl() + path, payload, options);
  }

  static async postLocal(path: any, payload: any) {

    const options = await BaseApi.getOptions(undefined)
    const request = BaseApi.applyRequestContext(options, payload)
    return axios
        .post( BaseApi.getLocalBaseUrl() + path, request.payload, request.options);
  }

  static async post(path: any, payload: any) {

    const options = await BaseApi.getOptions(undefined);
    const request = BaseApi.applyRequestContext(options, payload)
    return axios
        .post(applicationApiPath(path,apiAzureUrl()), request.payload, request.options);
  }

  // UPT-1848 Phase B2 : upload multipart (analyse IA de document, ex. "Analyser un
  // document" sur le formulaire Contract). BaseApi.post() force toujours
  // Content-Type: application/json (voir getHeaders) — inadapté à un FormData, dont
  // axios doit fixer lui-meme le boundary multipart/form-data. On reprend les memes
  // en-tetes (auth + tenant/workspace via buildSaasHeaders) que post(), sans le
  // Content-Type impose.
  static async postMultipart(path: any, formData: FormData) {

    const preferredAccessToken = await getPreferredAccessToken();
    const headers = BaseApi.getHeaders(preferredAccessToken);
    delete headers['Content-Type'];

    const options: any = {
      mode: "cors",
      credentials: 'same-origin',
      headers
    };

    return axios
        .post(applicationApiPath(path,apiAzureUrl()), formData, options);
  }

  static async put(path: any, payload: any) {

    const options = await BaseApi.getOptions(undefined);
    const request = BaseApi.applyRequestContext(options, payload)
    return axios
        .put(applicationApiPath(path,apiAzureUrl()), request.payload, request.options);
  }

  static async patch(path: any, payload: any) {
    const options = await BaseApi.getOptions(undefined);
    const request = BaseApi.applyRequestContext(options, payload);
    return axios
        .patch(applicationApiPath(path,apiAzureUrl()), request.payload, request.options);
  }

  static async delete(path: any, payload?: any) {

    // axios.delete(url, config) expects config = { data, headers, params, ... }
    const options = await BaseApi.getOptions(payload);
    const request = BaseApi.applyRequestContext(options, payload);
    const config: any = {
      data: request.payload,
      headers: request.options.headers,
    };

    // support query-string style payloads if caller passes them
    if (request.options.params) {
      config.params = request.options.params;
      config.paramsSerializer = request.options.paramsSerializer;
    }

    return axios.delete(applicationApiPath(path,apiAzureUrl()), config);
  }

  static async deleteLocal(path: any, payload?: any) {

    const options = await BaseApi.getOptions(payload);
    const request = BaseApi.applyRequestContext(options, payload);
    const config: any = {
      data: request.payload,
      headers: request.options.headers,
    };

    if (request.options.params) {
      config.params = request.options.params;
      config.paramsSerializer = request.options.paramsSerializer;
    }

    return axios.delete(BaseApi.getLocalBaseUrl() + path, config);
  }

  static json(response: AxiosResponse<any>) {
    return response.data;
  }

  static handleResponse(response: any) {
    if (response.status == 200 || response.status == 201) {

      return response.json().then((response: any) => {
        return response;
      });
    } else {
      throw new Error(`Invalid response code: [${response.status}]`)
    }
  }
}


// static fetch(path, options) {
//   const defaultOptions = {
//     mode: 'cors',
//     credentials: 'same-origin'
//   };
//   return fetch(path, Object.assign({}, defaultOptions, options));
// }
//
// static post(path, options) {
//   const defaultOptions = {
//     mode: 'cors',
//     credentials: 'same-origin',
//     method: 'POST'
//   };
//   return fetch(path, Object.assign({}, defaultOptions, options));
// }
//
// static postLocal(path, data) {
//
//   const defaultOptions = {
//     mode: 'cors',
//     headers: {
//       'Accept': 'application/json',
//       'Content-Type': 'application/json',
//       'Access-Control-Allow-Origin': '"'
//     }
//   }
//   return axios
//     .post("http://localhost:8080" + path, data, defaultOptions);
// }
